import { useEffect, useSyncExternalStore } from "react";

import {
  createCmmdEnvironmentListTransport,
  type CmmdEnvironmentListTransport,
  type CmmdEnvironmentListTransportEvent,
} from "./cmmdEnvironmentListTransport";
import type { CmmdEnvironmentListSnapshot } from "./cmmdEnvironmentListTypes";

export type { CmmdEnvironmentListSnapshot } from "./cmmdEnvironmentListTypes";

type Listener = () => void;

const INITIAL_SNAPSHOT: CmmdEnvironmentListSnapshot = {
  isLoading: false,
  error: null,
  environments: [],
  stale: false,
};

interface CmmdEnvironmentListResource {
  readonly subscribe: (listener: Listener) => () => void;
  readonly read: () => CmmdEnvironmentListSnapshot;
  readonly initialize: () => void;
  readonly refresh: () => void;
  readonly _teardown: () => void;
  readonly _setState: (next: CmmdEnvironmentListSnapshot) => void;
}

/**
 * Singleton store for CMMD's unioned environment picker payload. The transport
 * keeps the list fresh over SSE; readers subscribe with
 * `useCmmdEnvironmentListSnapshot`. One resource, not one per surface, so the
 * settings page and any future picker share a single stream.
 */
function createCmmdEnvironmentListResource(): CmmdEnvironmentListResource {
  let snapshot: CmmdEnvironmentListSnapshot = INITIAL_SNAPSHOT;
  const listeners = new Set<Listener>();

  function emit(next: CmmdEnvironmentListSnapshot): void {
    snapshot = next;
    for (const listener of listeners) listener();
  }

  function reduceTransportEvent(event: CmmdEnvironmentListTransportEvent): void {
    switch (event.kind) {
      case "loading":
        emit({ ...snapshot, isLoading: true, error: null });
        return;
      case "environments":
        emit({
          isLoading: false,
          error: null,
          environments: event.environments,
          stale: false,
        });
        return;
      case "failed":
        emit({
          ...snapshot,
          isLoading: false,
          error: event.message,
          stale: snapshot.environments.length > 0,
        });
        return;
      case "auth-refreshing":
        emit({
          ...snapshot,
          isLoading: true,
          error: null,
          stale: snapshot.environments.length > 0,
        });
        return;
      case "auth-required":
        emit({
          ...snapshot,
          isLoading: false,
          error: event.message,
          stale: snapshot.environments.length > 0,
        });
        return;
    }
  }

  const transport: CmmdEnvironmentListTransport = createCmmdEnvironmentListTransport({
    onEvent: reduceTransportEvent,
  });

  return {
    subscribe(listener: Listener): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    read(): CmmdEnvironmentListSnapshot {
      return snapshot;
    },
    initialize(): void {
      transport.initialize();
    },
    refresh(): void {
      transport.refresh();
    },
    _teardown(): void {
      transport.teardown();
    },
    _setState(next: CmmdEnvironmentListSnapshot): void {
      emit(next);
    },
  };
}

let singleton: CmmdEnvironmentListResource | null = null;

function getSingleton(): CmmdEnvironmentListResource {
  if (singleton === null) {
    singleton = createCmmdEnvironmentListResource();
  }
  return singleton;
}

export function refreshCmmdEnvironmentList(): void {
  getSingleton().refresh();
}

function subscribeCmmdEnvironmentList(listener: Listener): () => void {
  return getSingleton().subscribe(listener);
}

function readCmmdEnvironmentListSnapshot(): CmmdEnvironmentListSnapshot {
  return getSingleton().read();
}

/** The unioned environment list, kept fresh by the resource's own SSE stream.
 * Mounting initializes the stream passively and idempotently. */
export function useCmmdEnvironmentListSnapshot(): CmmdEnvironmentListSnapshot {
  useEffect(() => {
    getSingleton().initialize();
  }, []);
  return useSyncExternalStore(
    subscribeCmmdEnvironmentList,
    readCmmdEnvironmentListSnapshot,
    readCmmdEnvironmentListSnapshot,
  );
}
